import { graphql } from '@shared/types/storefront'

graphql(/* GraphQL */ `
  fragment ProductVariant on ProductVariant {
    id
    sku
    availableForSale
    gtin: barcode
    compareAtPrice {
      amount
    }
    price {
      amount
      currencyCode
    }
    selectedOptions {
      name
      value
    }
  }
`)

graphql(/* GraphQL */ `
  fragment ProductVariantWithProduct on ProductVariant {
    ...ProductVariant
    product {
      ...Product
    }
  }
`)

graphql(/* GraphQL */ `
  fragment Filter on Filter {
    id
    label
    presentation
    type
    values {
      count
      id
      input
      label
    }
  }
`)

graphql(`
  fragment Product on Product {
    id
    title
    handle
    descriptionHtml
    availableForSale
    seo {
      description
      title
    }
    variantsCount {
      count
    }
    collections(first: 10) {
      items: nodes {
        ...Collection
      }
    }
    metafields(
      identifiers: [
        { key: "battery_un_number", namespace: "pim" }
        { key: "care_instructions", namespace: "pim" }
        { key: "caseable_sku", namespace: "pim" }
        { key: "collection", namespace: "pim" }
        { key: "colors", namespace: "pim" }
        { key: "delivery_time", namespace: "pim" }
        { key: "depth", namespace: "pim" }
        { key: "description_short", namespace: "pim" }
        { key: "edition", namespace: "pim" }
        { key: "feature_description", namespace: "pim" }
        { key: "features", namespace: "pim" }
        { key: "height", namespace: "pim" }
        { key: "instructions_for_use", namespace: "pim" }
        { key: "launch_date", namespace: "pim" }
        { key: "material_description", namespace: "pim" }
        { key: "material_type", namespace: "pim" }
        { key: "model", namespace: "pim" }
        { key: "name", namespace: "pim" }
        { key: "new", namespace: "pim" }
        { key: "product_content_collection", namespace: "pim" }
        { key: "responsibility", namespace: "pim" }
        { key: "restock_date", namespace: "pim" }
        { key: "season", namespace: "pim" }
        { key: "style", namespace: "pim" }
        { key: "style_switcher_color_code", namespace: "pim" }
        { key: "sub_category", namespace: "pim" }
        { key: "theme_world", namespace: "pim" }
        { key: "type", namespace: "pim" }
        { key: "volume", namespace: "pim" }
        { key: "weight", namespace: "pim" }
        { key: "width", namespace: "pim" }
        { key: "videos", namespace: "pim" }
        { key: "promoted_products", namespace: "custom" }
        { key: "style_untranslated", namespace: "custom" }
        { key: "model_untranslated", namespace: "custom" }
        { key: "hreflang", namespace: "flow" }
        { key: "canonical", namespace: "flow" }
      ]
    ) {
      type
      key
      value
    }
    featuredImage {
      url
      altText
      thumbhash
    }
    images(first: 20) {
      items: nodes {
        id
        url
        thumbhash
        altText
        height
        width
      }
    }
  }

  fragment ProductWithVariants on Product {
    ...Product
    variants(first: 50) {
      items: nodes {
        ...ProductVariant
      }
    }
  }
`)

graphql(/* GraphQL */ `
  fragment SearchResult on SearchResultItemConnection {
    items: nodes {
      ... on Product {
        ...ProductWithVariants
      }
    }
    pageInfo {
      ...PageInfo
    }
    totalCount
    productFilters {
      id
      label
      presentation
      type
      values {
        count
        id
        input
        label
      }
    }
  }
`)

export const productQuery = graphql(/* GraphQL */ `
  query Product($handle: String, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      ...ProductWithVariants
    }
  }
`)

export const productByIdQuery = graphql(/* GraphQL */ `
  query ProductById($id: ID!, $country: CountryCode, $language: LanguageCode)
  @inContext(country: $country, language: $language) {
    product(id: $id) {
      ...ProductWithVariants
    }
  }
`)

export const productsQuery = graphql(/* GraphQL */ `
  query Products(
    $query: String!
    $country: CountryCode
    $language: LanguageCode
    $ipp: Int = 50
    $filter: [ProductFilter!]
    $sort: SearchSortKeys
  ) @inContext(country: $country, language: $language) {
    search(
      query: $query
      first: $ipp
      productFilters: $filter
      sortKey: $sort
    ) {
      items: nodes {
        ... on Product {
          ...ProductWithVariants
        }
      }
    }
  }
`)

export const productsByMetafield = graphql(/* GraphQL */ `
  query ProductsByMetafield(
    $filter: [ProductFilter!]
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    search(
      query: ""
      first: 50
      productFilters: $filter
      unavailableProducts: LAST
    ) {
      items: nodes {
        ... on Product {
          ...ProductWithVariants
        }
      }
    }
  }
`)

export const productsByCategoryQuery = graphql(/* GraphQL */ `
  query ProductsByCategory(
    $categoryId: ID!
    $first: Int = 50
    $country: CountryCode
    $language: LanguageCode
    $sort: ProductCollectionSortKeys
  ) @inContext(country: $country, language: $language) {
    collection(id: $categoryId) {
      products(first: $first, sortKey: $sort) {
        items: nodes {
          ...ProductWithVariants
        }
      }
    }
  }
`)

export const searchSuggestionsQuery = graphql(/* GraphQL */ `
  query SearchSuggestions(
    $pageQuery: String!
    $productQuery: String!
    $collectionQuery: String!
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collectionResults: predictiveSearch(
      query: $collectionQuery
      limit: 5
      types: [COLLECTION]
      searchableFields: [AUTHOR, BODY, TITLE]
    ) {
      collections {
        ...Collection
      }
    }
    pageResults: predictiveSearch(
      query: $pageQuery
      limit: 5
      types: [PAGE]
      searchableFields: [AUTHOR, BODY, TITLE]
    ) {
      pages {
        ...InfoPage
      }
    }
    productResults: predictiveSearch(
      query: $productQuery
      limit: 5
      unavailableProducts: LAST
      types: [PRODUCT]
      searchableFields: [
        AUTHOR
        BODY
        PRODUCT_TYPE
        TITLE
        VARIANTS_BARCODE
        VARIANTS_SKU
        VARIANTS_TITLE
      ]
    ) {
      products {
        ...Product
      }
    }
  }
`)

export const forwardSearchQuery = graphql(/* GraphQl */ `
  query search(
    $query: String!
    $filter: [ProductFilter!]
    $ipp: Int
    $sort: SearchSortKeys
    $desc: Boolean
    $cursor: String
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    search(
      productFilters: $filter
      query: $query
      first: $ipp
      unavailableProducts: LAST
      types: [PRODUCT]
      sortKey: $sort
      reverse: $desc
      after: $cursor
    ) {
      ...SearchResult
    }

    baseFilterSearch: search(
      query: $query
      first: $ipp
      unavailableProducts: LAST
      types: [PRODUCT]
      sortKey: $sort
      reverse: $desc
      after: $cursor
    ) {
      productFilters {
        id
        label
        presentation
        type
        values {
          count
          id
          input
          label
        }
      }
    }
  }
`)

export const backwardSearchQuery = graphql(/* GraphQL */ `
  query prevSearch(
    $query: String!
    $filter: [ProductFilter!]
    $ipp: Int
    $sort: SearchSortKeys
    $desc: Boolean
    $cursor: String
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    search(
      productFilters: $filter
      query: $query
      last: $ipp
      unavailableProducts: LAST
      types: [PRODUCT]
      sortKey: $sort
      reverse: $desc
      before: $cursor
    ) {
      ...SearchResult
    }

    baseFilterSearch: search(
      query: $query
      last: $ipp
      unavailableProducts: LAST
      types: [PRODUCT]
      sortKey: $sort
      reverse: $desc
      before: $cursor
    ) {
      productFilters {
        id
        label
        presentation
        type
        values {
          count
          id
          input
          label
        }
      }
    }
  }
`)
